import * as XLSX from 'xlsx'

/**
 * Exportador del plano de carga a SAP.
 *
 * Genera lo mismo que hoy se arma a mano en `PLANTILLA PLANOS.xlsx`:
 *   - XLSX con una hoja por tabla SAP (807, 808, 802, 809, ZPPU, 816, 887, 879…), replicando el layout
 *   - TXT por tabla, que es lo que realmente consume LSMW
 *
 * Layout replicado (verificado contra el archivo real):
 *   fila 1: C = válido hasta (99991231) · D = válido desde (hoy) · I.. = rótulos
 *   fila 2: A='C' B='A' C=<tabla> D='V' E=<condición> F=<moneda> · I.. = rótulos de los campos humanos
 *   fila 3+: A='I' · B=llave concatenada · C=99991231 · D=yyyymmdd · E=valor · F=moneda
 *            y a la derecha los campos que se digitan (org, lista/grupo/destinatario, material, valor)
 *
 * La columna H ('GRUPO Precio', que iba fija en 10) se QUITÓ: no la pide la carga. Al sacarla, todo
 * lo que estaba a su derecha corre una posición a la izquierda — los rótulos y sus valores arrancan
 * ahora en I y no en J.
 *
 * OJO — supuestos del TXT, pendientes de confirmar con quien carga en SAP:
 *   delimitador (por defecto TABULACIÓN, que es lo que sale al guardar el Excel como .txt),
 *   si van las columnas A–F o A–G, y si la cabecera va incluida.
 *   Los tres son parámetros: cuando se confirme, no hay que reescribir nada.
 */

const TRIM = (v) => String(v === null || v === undefined ? '' : v).trim()

/** Agrupa las líneas por tabla SAP respetando el orden real de carga. */
export const agruparPorTabla = (lineas) => {
  const mapa = new Map()
  lineas.forEach((l) => {
    const clave = `${l.tabla_sap}|${l.condicion}`
    if (!mapa.has(clave)) {
      mapa.set(clave, {
        tabla: l.tabla_sap,
        condicion: l.condicion,
        transaccion: l.transaccion,
        orden: Number(l.orden || 999),
        filas: [],
      })
    }
    mapa.get(clave).filas.push(l)
  })
  return [...mapa.values()].sort((a, b) => a.orden - b.orden)
}

/** Rótulos de los campos que se digitan, según lo que pide cada tabla. */
const rotulosDerecha = (grupo) => {
  const f = grupo.filas[0] || {}
  const cols = ['Org. Venta']
  if (TRIM(f.tipo_lista)) cols.push('Tipo lista')
  if (TRIM(f.grupo_precio)) cols.push('Grupo precio')
  if (TRIM(f.grupo_descuento)) cols.push('Grupo descuento')
  if (TRIM(f.clase_documento)) cols.push('Clase documento')
  if (TRIM(f.destinatario)) cols.push('Destinatario')
  cols.push('Producto SAP')
  cols.push(f.col_F === '%' ? '%' : 'Precio')
  return cols
}

const valoresDerecha = (fila) => {
  const vals = [TRIM(fila.organizacion)]
  if (TRIM(fila.tipo_lista)) vals.push(TRIM(fila.tipo_lista))
  if (TRIM(fila.grupo_precio)) vals.push(TRIM(fila.grupo_precio))
  if (TRIM(fila.grupo_descuento)) vals.push(TRIM(fila.grupo_descuento))
  if (TRIM(fila.clase_documento)) vals.push(TRIM(fila.clase_documento))
  if (TRIM(fila.destinatario)) vals.push(TRIM(fila.destinatario))
  vals.push(TRIM(fila.material))
  vals.push(Number(fila.col_E || 0))
  return vals
}

/**
 * 'yyyymmdd' -> número de serie de Excel (días desde 1899-12-30). 99991231 -> 2958465, que es lo que
 * trae la plantilla que SÍ sube a SAP (KAIROS). Si no es una fecha válida, null.
 */
export const fechaASerialExcel = (yyyymmdd) => {
  const m = /^(\d{4})(\d{2})(\d{2})$/.exec(TRIM(yyyymmdd))
  if (!m) return null
  const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return Number.isNaN(ms) ? null : Math.round(ms / 86400000) + 25569
}

// Igual que la plantilla que SAP acepta: los rótulos y campos digitados arrancan en J. G, H e I van
// vacías (la H 'GRUPO Precio' ya no se manda, pero las columnas no se corren).
const COL_DERECHA = 9

/**
 * Hoja con el mismo layout Y los mismos tipos de celda que la plantilla que SAP sí carga
 * (`docs/Plantillas completas precios  KAIROS_ 1.xlsx`):
 *   fila 1: C = válido hasta · D = válido desde (fechas, formato m/d/yy)
 *   fila 2: A='C' B='A' C=<tabla, número> D='V' E=<condición> F=<moneda> · J.. = rótulos
 *   fila 3+: A='I' B=llave C/D=fechas formato yyyymmdd E=valor F=moneda · J.. = campos digitados
 * C y D son FECHAS de Excel, no texto; E va redondeado a entero cuando es plata (ROUND(…,0) en la
 * plantilla) y las celdas vacías no se escriben.
 */
export const construirHoja = (grupo) => {
  const f0 = grupo.filas[0] || {}
  const ws = {}
  let maxCol = 5
  const poner = (fila, col, celda) => {
    if (!celda) return
    ws[XLSX.utils.encode_cell({ r: fila, c: col })] = celda
    if (col > maxCol) maxCol = col
  }
  const texto = (v) => (TRIM(v) === '' ? null : { t: 's', v: TRIM(v) })
  const numero = (v, z) => (Number.isFinite(v) ? { t: 'n', v, ...(z ? { z } : {}) } : null)
  const fecha = (v, z) => {
    const s = fechaASerialExcel(v)
    return s === null ? texto(v) : { t: 'n', v: s, z }
  }
  const esPct = (f0.col_F || 'COP') === '%'
  const valor = (l) => (esPct ? Number(l.col_E || 0) : Math.round(Number(l.col_E || 0)))
  const tabla = TRIM(grupo.tabla)

  poner(0, 2, fecha(f0.col_C, 'm/d/yy'))
  poner(0, 3, fecha(f0.col_D, 'm/d/yy'))

  poner(1, 0, texto('C'))
  poner(1, 1, texto('A'))
  poner(1, 2, /^\d+$/.test(tabla) ? numero(Number(tabla)) : texto(tabla))
  poner(1, 3, texto('V'))
  poner(1, 4, texto(grupo.condicion))
  poner(1, 5, texto(f0.col_F || 'COP'))
  rotulosDerecha(grupo).forEach((r, i) => poner(1, COL_DERECHA + i, texto(r)))

  grupo.filas.forEach((l, i) => {
    const fila = 2 + i
    poner(fila, 0, texto(l.col_A))
    poner(fila, 1, texto(l.col_B))
    poner(fila, 2, fecha(l.col_C, 'yyyymmdd'))
    poner(fila, 3, fecha(l.col_D, 'yyyymmdd'))
    poner(fila, 4, numero(valor(l)))
    poner(fila, 5, texto(l.col_F || 'COP'))
    valoresDerecha(l).forEach((v, j) => {
      poner(fila, COL_DERECHA + j, typeof v === 'number' ? numero(v, '0') : texto(v))
    })
  })

  ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: 1 + grupo.filas.length, c: maxCol } })
  return ws
}

/** Libro con una hoja por tabla SAP. Separado de la descarga para poder probarlo sin navegador. */
export const construirLibro = (lineas) => {
  const grupos = agruparPorTabla(lineas)
  const wb = XLSX.utils.book_new()
  grupos.forEach((g) => {
    // el nombre de hoja de Excel admite máximo 31 caracteres
    const nombre = `${g.tabla}${g.condicion === 'ZPPU' ? '-ZPPU' : g.condicion === 'ZDPC' ? '-ZDPC' : ''}`.slice(0, 31)
    XLSX.utils.book_append_sheet(wb, construirHoja(g), nombre)
  })
  return { wb, hojas: grupos.length }
}

/**
 * XLSX con una hoja por tabla SAP. `bookSST: true` es obligatorio: sin él SheetJS escribe los textos
 * como `t="str"` (tipo de resultado de fórmula) y sin sharedStrings.xml, y el lector de Excel de SAP
 * no los toma como los de un archivo guardado por Excel: la carga falla.
 */
export const descargarXlsx = (lineas, nombreBase) => {
  const { wb, hojas } = construirLibro(lineas)
  if (!hojas) return 0
  XLSX.writeFile(wb, `${nombreBase}.xlsx`, { bookType: 'xlsx', bookSST: true, compression: true })
  return hojas
}

/**
 * TXT por tabla: es lo que LSMW lee. Una fila de cabecera + N registros — el paso 3 del asistente
 * reporta "N datos más uno de cabecera", así que el conteo se puede cruzar contra el nombre del archivo.
 */
export const construirTxt = (grupo, opciones = {}) => {
  const delim = opciones.delim || '\t'
  // 'AH' se conserva como valor por compatibilidad con lo que ya está guardado en pantalla, pero
  // ahora llega hasta G: la columna H (GRUPO Precio) se quitó del plano.
  const hastaG = opciones.cols === 'AH' || opciones.cols === 'AG'
  const conCabecera = opciones.cabecera !== false

  const corte = hastaG ? 7 : 6 // A–F = 6 columnas · A–G = 7
  const cab = ['C', 'A', 'Tabla', 'V', 'Condicion', 'Moneda', 'Reservado'].slice(0, corte)

  const filas = grupo.filas.map((l) => {
    const base = [
      TRIM(l.col_A),
      TRIM(l.col_B),
      TRIM(l.col_C),
      TRIM(l.col_D),
      String(Number(l.col_E || 0)),
      l.col_F || 'COP',
      '',
    ].slice(0, corte)
    return base.join(delim)
  })

  const out = conCabecera ? [cab.join(delim), ...filas] : filas
  return out.join('\r\n') // CRLF: el archivo se abre y se carga desde Windows
}

/** Dispara la descarga de un texto como archivo. */
const bajarTexto = (texto, nombre, conBom) => {
  // BOM explícito por código: algunos cargadores en Windows lo piden para leer bien las tildes
  const contenido = conBom ? String.fromCharCode(0xFEFF) + texto : texto
  const blob = new Blob([contenido], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = nombre
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/**
 * Un TXT por tabla, en el orden real de carga y con el conteo en el nombre
 * (ej. `807_ZPAU_20260727_156reg.txt`) para poder cruzarlo con lo que reporta LSMW.
 */
export const descargarTxt = (lineas, opciones = {}) => {
  const grupos = agruparPorTabla(lineas)
  if (!grupos.length) return []
  const fecha = (grupos[0].filas[0] || {}).col_D || ''
  const generados = []
  // `desfase`: si se llama varias veces seguidas (una tanda por fecha), cada tanda arranca después
  // de la anterior en vez de dispararse todas a la vez.
  const desfase = Number(opciones.desfase || 0)

  grupos.forEach((g, i) => {
    const nombre = `${g.tabla}_${g.condicion}_${fecha}_${g.filas.length}reg.txt`
    // se escalonan las descargas: varias simultáneas las bloquea el navegador
    setTimeout(() => bajarTexto(construirTxt(g, opciones), nombre, opciones.bom), desfase + i * 350)
    generados.push({ nombre, registros: g.filas.length, tabla: g.tabla, transaccion: g.transaccion })
  })

  return generados
}

/** Llaves con espacio: rompen la carga en SAP. Se revisa antes de dejar descargar. */
export const validarLineas = (lineas) => {
  const problemas = []
  lineas.forEach((l) => {
    const llave = String(l.col_B || '')
    if (llave !== llave.trim()) problemas.push(`${l.material}: la llave tiene espacios al inicio o final`)
    if (!TRIM(l.material)) problemas.push('Hay una línea sin código de material')
    if (String(l.material || '') !== String(l.material || '').trim()) {
      problemas.push(`${l.material}: el código de material tiene espacios`)
    }
  })
  return [...new Set(problemas)]
}
